<template>
  <section v-if="settings && pages" :class="{ 'app-formie-form-theme--lid': $route.meta.style === 'lid' }" class="px-[2px]">
    <div class="relative z-10" :class="{ 'bg-white rounded-b-3xl sm:rounded-3xl': $route.meta.style === 'db' }">
      <transition name="fade">
        <FormKit
          v-show="!(settings.submitActionFormHide && submissionState === 'ok')"
          id="FormieForm"
          v-slot="{ disabled, state: { loading } }"
          type="form"
          :actions="false"
          :value="normalizedInitialValue"
          @submit-invalid="scrollToErrors"
          @submit="submit"
        >
          <h3 v-if="settings.displayFormTitle" class="text-bold text-2xl">{{ title }}</h3>

          <!-- Pages -->
          <ul>
            <li v-for="(page, pageIndex) of pages" v-show="pageIndex + 1 === currentPage" :key="page.id" class="mb-12">
              <h3 v-if="settings.displayCurrentPageTitle" class="text-bold text-lg">{{ page.name }}</h3>

              <!--
               Note that the following element is a copy from FormieRows.vue, we need this here because otherwise
               formulate does not properly propagate the initial value, when given
               -->
              <ul>
                <li v-for="row of page.rows" v-show="rowIsVisible(row)" :key="row.id" class="mb-4">
                  <ul
                    :class="{
                      'md:grid md:gap-6': row.fields.length > 1,
                      'md:grid-cols-2': row.fields.length === 2,
                      'md:grid-cols-3': row.fields.length === 3,
                      'md:grid-cols-4': row.fields.length === 4,
                      'md:grid-cols-5': row.fields.length === 5,
                      'md:grid-cols-6': row.fields.length === 6,
                    }"
                  >
                    <li v-for="field of row.fields" :key="field.id">
                      <component :is="componentMap[field.__typename]" :settings="settings" :field="field" />
                    </li>
                  </ul>
                </li>
                <li class="mb-4">
                  <template v-if="pages.length > 1">
                    <FormKit
                      v-show="pageIndex < pages.length - 1"
                      :disabled="loading || disabled || submissionState === 'ok'"
                      type="button"
                      :label="page.settings.submitButtonLabel"
                      class="inline-block"
                      @click="currentPage++"
                    />
                    <FormKit
                      v-show="pageIndex > 0"
                      :disabled="loading || disabled || submissionState === 'ok'"
                      type="button"
                      :label="page.settings.backButtonLabel"
                      class="inline-block"
                      @click="currentPage--"
                    />
                  </template>
                  <FormKit v-show="pageIndex === pages.length - 1" :disabled="loading || disabled || submissionState === 'ok'" type="submit">
                    <span v-show="(loading || disabled) && settings.loadingIndicator">
                      <AtomIcon icon="app-loader" class="mr-2 w-5 h-5 text-white" />
                    </span>
                    <span v-show="(loading || disabled) && settings.loadingIndicator && settings.loadingIndicatorText" v-html="settings.loadingIndicatorText" />
                    <span v-show="!((loading || disabled) && settings.loadingIndicator && settings.loadingIndicatorText)">{{ page.settings.submitButtonLabel }}</span>
                  </FormKit>
                </li>
              </ul>
            </li>
          </ul>
        </FormKit>
      </transition>

      <div v-if="submissionState === 'expired'" class="p-4 mb-4 text-red-600 bg-red-50 rounded">Dit formulier is verlopen en kan niet meer worden ingediend.</div>
      <div v-show="submissionState === 'ok'" v-html="settings.submitActionMessageHtml" />
    </div>
  </section>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'
import flatten from 'lodash/flatten'
import { ref, unref, resolveComponent, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import type { FormKitNode } from '@formkit/core'
import { getValidationMessages } from '@formkit/validation'
import { fieldVisible } from '~/assets/formie'
import type { FormieSettings, FormieField } from '~/assets/formie'
import { linkDetails } from '~/composables/link'
import { useLinkRewrites } from '~/composables/craft'
import { useGraphqlFetch } from '~/composables/craft-fetch'

interface Props {
  combinedSizeLimit?: number
  enableEntriesField?: boolean
  enableFileUploadField?: boolean
  handle: string
  initialValue?: Record<string, any>
  expiryDate?: string
}

const props = withDefaults(defineProps<Props>(), {
  combinedSizeLimit: undefined,
  enableEntriesField: false,
  enableFileUploadField: true,
  initialValue: () => ({}),
  expiryDate: undefined,
})

const emit = defineEmits(['preSubmission'])

const router = useRouter()
const graphqlFetch = useGraphqlFetch()
const rewrites = useLinkRewrites()

const currentPage = ref(1)
const { title, settings, pages } = setupForm(props.handle, {
  combinedSizeLimit: props.combinedSizeLimit,
  enableEntriesField: props.enableEntriesField,
  enableFileUploadField: props.enableFileUploadField,
})
const { submissionState, submit } = setupSubmit(props.handle, settings, pages, emit)

const rowIsVisible = (row: any) => row.fields.every(fieldVisible)
const normalizedInitialValue = ref(cloneDeep(unref(props.initialValue)))

const componentMap = {
  Field_Agree: resolveComponent('FormieFieldAgree'),
  Field_Checkboxes: resolveComponent('FormieFieldCheckboxes'),
  Field_Date: resolveComponent('FormieFieldDate'),
  Field_Dropdown: resolveComponent('FormieFieldDropdown'),
  Field_Email: resolveComponent('FormieFieldEmail'),
  Field_Entries: resolveComponent('FormieFieldEntries'),
  Field_FileUpload: resolveComponent('FormieFieldFileUpload'),
  Field_Group: resolveComponent('FormieFieldGroup'),
  Field_Heading: resolveComponent('FormieFieldHeading'),
  Field_Hidden: resolveComponent('FormieFieldHidden'),
  Field_Html: resolveComponent('FormieFieldHtml'),
  Field_MultiLineText: resolveComponent('FormieFieldMultiLineText'),
  Field_Name: resolveComponent('FormieFieldName'),
  Field_Number: resolveComponent('FormieFieldNumber'),
  Field_Phone: resolveComponent('FormieFieldPhone'),
  Field_Radio: resolveComponent('FormieFieldRadio'),
  Field_Repeater: resolveComponent('FormieFieldRepeater'),
  Field_Section: resolveComponent('FormieFieldSection'),
  Field_SingleLineText: resolveComponent('FormieFieldSingleLineText'),
}

const scrollToErrors = (node: FormKitNode): void => {
  const messages = getValidationMessages(node)
  const first: FormKitNode | undefined = messages.keys().next().value
  const el = first ? document.querySelector(`#${first.context.id}[name="${first.name}"]`) : null
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

async function postProcess(fields: FormieField[], value: any) {
  const readFileAndDataURL = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  interface Match {
    key: string
    node: any
    type: string
    group?: string
    handle: string
  }

  // A recursive finder that locates nodes eligible for post-processing
  function finder(root: any): Match[] {
    const matches: Match[] = []

    function helper(node: any, path: (string | number)[]): void {
      if (node === undefined || node === null) {
        return
      }

      if (Array.isArray(node)) {
        for (let index = 0; index < node.length; index++) {
          // Apply recursion
          helper(node[index], [...path, index])
        }
        return
      }

      if (typeof node === 'object') {
        for (const [key, child] of Object.entries(node)) {
          helper(child, [...path, key])

          const match = key.match(/^__(?<type>Field_[a-z]+)(__(?<group>[a-z]+))?__(?<handle>[0-9a-z_]+)$/i)
          if (match?.groups) {
            matches.push({
              key,
              node,
              type: match.groups.type,
              group: match.groups.group,
              handle: match.groups.handle,
            })
          }
        }
      }
    }

    helper(root, [])
    return matches
  }

  // Clone and find eligible nodes
  const clone = cloneDeep(value)
  const matches = finder(clone)

  // Make a map from the fields for easy access
  const fieldMap = Object.fromEntries([
    // Add every top-level field
    ...fields.map((field: FormieField) => [`__${field.__typename}__${field.handle}`, field]),
    // Add fields nested within Field_Group fields
    ...flatten(
      flatten(
        fields
          .filter((field: FormieField) => field.__typename === 'Field_Group')
          .map((field: FormieField) =>
            field.rows.map((row) => row.fields.map((nestedField: FormieField) => [`__${nestedField.__typename}__${field.handle}__${nestedField.handle}`, nestedField])),
          ),
      ),
    ),
  ])

  for (const { key, node, type, handle } of matches) {
    switch (type) {
      case 'Field_Agree':
        // For FormKit this is a boolean, while Formie expects a non-empty string for 'true' and an empty string for 'false'
        node[handle] = node[key] ? fieldMap[key]?.checkedValue || 'True' : ''
        break

      case 'Field_Checkboxes':
        // For FormKit this is `[]` when no options are selected, while Formie expects null when no options are selected
        node[handle] = Array.isArray(node[key]) && node[key].length === 0 ? null : node[key]
        break

      case 'Field_Dropdown':
        // For FormKit this is `[]` when no options are selected, while Formie expects null when no options are selected
        node[handle] = Array.isArray(node[key]) && node[key].length === 0 ? null : node[key]
        break

      case 'Field_Entries':
        // For FormKit this is either false or a string id, while Formie expects either null or an integer
        node[handle] = node[key] ? Number.parseInt(node[key]) : null
        break

      case 'Field_Hidden':
        // For FormKit this can be any type, while Formie expects null or a string
        node[handle] = node[key] ? `${node[key]}` : null
        break

      case 'Field_FileUpload':
        // For FormKit this may be an array of File objects or {name, file}
        node[handle] = await Promise.all(
          (node[key] || []).map(async (value: any) => {
            const file = value?.file ?? value
            if (!file) return null
            return {
              filename: file.name,
              fileData: await readFileAndDataURL(file),
            }
          }),
        )
        break

      case 'Field_Group':
        // For FormKit this is an array with one object, while Formie expects only the object
        node[handle] = node[key]?.[0]
        break

      case 'Field_Name':
        // For FormKit this is an array with one object, while Formie expects only the object (depending on the useMultipleFields setting)
        if (fieldMap[key]?.useMultipleFields) {
          node[handle] = Array.isArray(node[key]) ? node[key]?.[0] : node[key]
        } else {
          node[handle] = node[key]
        }
        break

      case 'Field_Number':
        // For FormKit this is a string representing a number, while Formie expects a float, integer, or null
        if (node[key] && node[key].length) {
          if (Number.parseInt(fieldMap[key]?.decimals || '0')) {
            node[handle] = Number.parseFloat(node[key])
          } else {
            node[handle] = Number.parseInt(node[key])
          }
        } else {
          node[handle] = null
        }
        break

      case 'Field_Radio':
        // For FormKit this is `false` when no option is selected, while Formie expects null when no option is selected
        node[handle] = node[key] === false ? null : node[key]
        break

      case 'Field_Repeater':
        // For FormKit this is an array with objects, while Formie expects an object with a property 'rows' that contains the array with objects
        node[handle] = { rows: node[key] }
        break

      default:
        node[handle] = node[key]
        break
    }
    delete node[key]
  }

  return clone
}

function setupForm(
  handle: string,
  { combinedSizeLimit, enableEntriesField, enableFileUploadField }: { combinedSizeLimit?: number; enableEntriesField: boolean; enableFileUploadField: boolean },
) {
  const title = ref<string | undefined>(undefined)
  const settings = ref<FormieSettings>({})
  const pages = ref<any[]>([])

  useFetch(
    async () => {
      const settingsFragment = `
        fragment settingsFragment on FormInterface {
          settings {
            displayFormTitle
            displayCurrentPageTitle
            defaultLabelPosition
            defaultInstructionsPosition

            errorMessageHtml
            redirectUrl
            submitAction
            submitActionFormHide
            submitActionMessageHtml
            submitActionMessageTimeout
            submitActionTab

            loadingIndicator
            loadingIndicatorText
          }
        }`

      const fieldAgreeFragment = `
        fragment fieldAgreeFragment on Field_Agree {
          descriptionHtml
          checkedValue
          defaultState
          name
          uncheckedValue
        }`

      const fieldCheckboxesFragment = `
        fragment fieldCheckboxesFragment on Field_Checkboxes {
          layout
          options {
            isDefault
            label
            value
          }
        }`

      const fieldDateFragment = `
        fragment fieldDateFragment on Field_Date {
          defaultDate
          includeTime
          minDate
          maxDate
        }`

      const fieldDropdownFragment = `
        fragment fieldDropdownFragment on Field_Dropdown {
          multi
          options {
            isDefault
            label
            value
          }
        }`

      const fieldEmailFragment = `
        fragment fieldEmailFragment on Field_Email {
          defaultValue
        }`

      const fieldEntriesFragment = `
        fragment fieldEntriesFragment on Field_Entries {
          entries {
            id
            title
          }
        }`

      const fieldFileUploadFragment = `
        fragment fieldFileUploadFragment on Field_FileUpload {
          allowedKinds
          limitFiles
          sizeLimit
          sizeMinLimit
          volumeHandle
        }`

      const fieldHeadingFragment = `
        fragment fieldHeadingFragment on Field_Heading {
          headingSize
        }`

      const fieldHiddenFragment = `
        fragment fieldHiddenFragment on Field_Hidden {
          defaultOption
          defaultValue
        }`

      const fieldHtmlFragment = `
        fragment fieldHtmlFragment on Field_Html {
          htmlContent
        }`

      const fieldMultiLineTextFragment = `
        fragment fieldMultiLineTextFragment on Field_MultiLineText {
          defaultValue
          minLength: min
          minType
          maxLength: max
          maxType
        }`

      const fieldNameFragment = `
        fragment fieldNameFragment on Field_Name {
          defaultValue
          useMultipleFields

          prefixEnabled
          prefixLabel
          prefixPlaceholder
          prefixDefaultValue
          prefixRequired
          prefixErrorMessage
          prefixOptions {
            label
            value
          }

          firstNameEnabled
          firstNameLabel
          firstNamePlaceholder
          firstNameDefaultValue
          firstNameRequired
          firstNameErrorMessage

          middleNameEnabled
          middleNameLabel
          middleNamePlaceholder
          middleNameDefaultValue
          middleNameRequired
          middleNameErrorMessage

          lastNameEnabled
          lastNameLabel
          lastNamePlaceholder
          lastNameDefaultValue
          lastNameRequired
          lastNameErrorMessage
        }`

      const fieldNumberFragment = `
        fragment fieldNumberFragment on Field_Number {
          defaultValue
          limit
          minValue: min
          maxValue: max
          decimals
        }`

      const fieldPhoneFragment = `
        fragment fieldPhoneFragment on Field_Phone {
          defaultValue
        }`

      const fieldRadioFragment = `
        fragment fieldRadioFragment on Field_Radio {
          layout
          options {
            isDefault
            label
            value
          }
        }`

      const fieldSectionFragment = `
        fragment fieldSectionFragment on Field_Section {
          borderStyle
          borderWidth
          borderColor
        }`

      const fieldSingleLineTextFragment = `
        fragment fieldSingleLineTextFragment on Field_SingleLineText {
          defaultValue
          minLength: min
          minType
          maxLength: max
          maxType
        }`

      const fieldProperties = `
        __typename
        id
        inputTypeName
        name
        handle
        labelPosition
        placeholder
        instructions
        instructionsPosition
        required
        errorMessage
        visibility`

      const fieldPropertyFragments = `
        ...fieldAgreeFragment
        ...fieldCheckboxesFragment
        ...fieldDateFragment
        ...fieldDropdownFragment
        ...fieldEmailFragment
        ${enableEntriesField ? '...fieldEntriesFragment' : ''}
        ${enableFileUploadField ? '...fieldFileUploadFragment' : ''}
        ...fieldHeadingFragment
        ...fieldHiddenFragment
        ...fieldHtmlFragment
        ...fieldMultiLineTextFragment
        ...fieldNameFragment
        ...fieldNumberFragment
        ...fieldPhoneFragment
        ...fieldRadioFragment
        ...fieldSectionFragment
        ...fieldSingleLineTextFragment`

      const pagesFragment = `
        fragment pagesFragment on FormInterface {
          title
          pages {
            id
            name
            settings {
              backButtonLabel
              buttonsPosition
              submitButtonLabel
            }
            rows {
              id
              fields: rowFields {
                ${fieldProperties}
                ${fieldPropertyFragments}
                ... on Field_Group {
                  rows: nestedRows {
                    fields: rowFields {
                      ${fieldProperties}
                      ${fieldPropertyFragments}
                    }
                  }
                }
                ... on Field_Repeater {
                  addLabel
                  minRows
                  maxRows
                  rows: nestedRows {
                    fields: rowFields {
                      ${fieldProperties}
                      ${fieldPropertyFragments}
                    }
                  }
                }
              }
            }
          }
        }`

      const query = `
        query setupForm($handle: [String]) {
          formieForm(handle: $handle) {
            ...settingsFragment
            ...pagesFragment
          }
        }
        ${settingsFragment}
        ${pagesFragment}
        ${fieldAgreeFragment}
        ${fieldCheckboxesFragment}
        ${fieldDateFragment}
        ${fieldDropdownFragment}
        ${fieldEmailFragment}
        ${enableEntriesField ? fieldEntriesFragment : ''}
        ${enableFileUploadField ? fieldFileUploadFragment : ''}
        ${fieldHeadingFragment}
        ${fieldHiddenFragment}
        ${fieldHtmlFragment}
        ${fieldMultiLineTextFragment}
        ${fieldNameFragment}
        ${fieldNumberFragment}
        ${fieldPhoneFragment}
        ${fieldRadioFragment}
        ${fieldSectionFragment}
        ${fieldSingleLineTextFragment}`

      const data = await graphqlFetch({ query, variables: { handle }, token: 'formie' })

      // Assign the query results to separate reactive properties
      title.value = data.formieForm.title
      settings.value = { ...data.formieForm.settings, combinedSizeLimit }
      pages.value = data.formieForm.pages
    },
    {
      key: `form-${handle}`,
    },
  )

  return {
    pages,
    settings,
    title,
  }
}

function setupSubmit(handle: string, settings: Ref<FormieSettings>, pages: Ref<any[]>, emit: any) {
  const submissionState = ref<'loading' | 'ok' | 'error' | undefined>(undefined)

  const submit = async (formData: any) => {
    submissionState.value = 'loading'

    // Check if form is expired
    if (props.expiryDate) {
      const expiryDate = new Date(props.expiryDate)
      const now = new Date()

      if (expiryDate < now) {
        submissionState.value = 'expired'
        return
      }
    }

    const fields: FormieField[] = flatten(flatten(unref(pages).map((page) => page.rows.map((row) => row.fields))))
    const mutationName = `save_${handle}_Submission`
    const mutationParameterDefinitions = fields.map((field: FormieField) => `$${field.handle}: ${field.inputTypeName}`).join(', ')
    const mutationParameters = fields.map((field: FormieField) => `${field.handle}: $${field.handle}`).join(', ')

    try {
      const query = `
        mutation saveSubmission(${mutationParameterDefinitions}) {
          ${mutationName}(${mutationParameters}) {
            uid
          }
        }`

      const variables = await postProcess(fields, formData)
      emit('preSubmission', variables)

      await graphqlFetch({ query, variables, token: 'formie' })

      submissionState.value = 'ok'

      // Check if we need to redirect
      const { redirectUrl, submitActionTab, submitAction } = unref(settings)
      const redirect = (targetUrl: string) => {
        if (!targetUrl) {
          return
        }
        const { scope, url } = linkDetails('//', targetUrl, { rewrites })
        if (submitActionTab === 'new-tab' || scope === 'outside') {
          window.open(url, submitActionTab === 'new-tab' ? '_blank' : '_self')
        } else {
          router.push({ path: url })
        }
      }

      switch (submitAction) {
        case 'reload':
          window.location.reload()
          break
        case 'entry':
        case 'url':
          redirect(redirectUrl)
          break
      }
    } catch (error) {
      console.error(error)
      submissionState.value = 'error'
    }
  }

  return {
    submissionState,
    submit,
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  @apply transition duration-150 transition-opacity ease-in-out;
}

.fade-enter,
.fade-leave-to {
  @apply opacity-0;
}
</style>
